import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';
import { AuthGate } from './components/AuthGate.js';
import { RouteErrorComponent } from './components/ErrorBoundary.js';
import { OnboardingWizard } from './features/onboarding/OnboardingWizard.js';
import { BookPage } from './features/book/index.js';
import { MyWeekPage } from './features/myweek/index.js';
import { AcceptInvitePage } from './routes/AcceptInvitePage.js';
import { ClientsPage } from './routes/ClientsPage.js';
import { CommsPage } from './routes/CommsPage.js';
import { DiaryPage } from './routes/DiaryPage.js';
import { ForgotPasswordPage } from './routes/ForgotPasswordPage.js';
import { PackagesPage } from './routes/PackagesPage.js';
import { PaymentsPage } from './routes/PaymentsPage.js';
import { QueuePage } from './routes/QueuePage.js';
import { ReportsPage } from './routes/ReportsPage.js';
import { ResetPasswordPage } from './routes/ResetPasswordPage.js';
import { ReviewsPage } from './routes/ReviewsPage.js';
import { SettingsPage } from './routes/SettingsPage.js';
import { StockPage } from './routes/StockPage.js';
import { TakingsPage } from './routes/TakingsPage.js';

function RootLayout() {
  // AuthGate renders the login screen when anonymous, otherwise the shell + <Outlet/>.
  return (
    <AuthGate>
      <Outlet />
    </AuthGate>
  );
}

const rootRoute = createRootRoute({ component: RootLayout });

// Explicit routes (not a helper) so each literal `path` is preserved for typed navigation.
const diaryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: DiaryPage,
});
const queueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/queue',
  component: QueuePage,
});
const clientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clients',
  // Retain + type the ?q= search param so a deep-link (the global command palette, the diary "view
  // client" action) reliably drives the clients filter — ClientsPage reads it reactively via useSearch.
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: typeof search.q === 'string' && search.q.length > 0 ? search.q : undefined,
  }),
  component: ClientsPage,
});
const takingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/takings',
  component: TakingsPage,
});
// WS6 — the payments/receipts BACK-OFFICE (refunds, receipt history, no-show fees). Manager-only in
// nav; the refund/void/no-show actions are themselves manager-gated on the API and in the UI.
const paymentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/payments',
  component: PaymentsPage,
});
const commsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/comms',
  component: CommsPage,
});
const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports',
  component: ReportsPage,
});
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsPage,
});
// WS7 — stock / products / suppliers. Manager-only in nav; every write is requireManager+stock:write
// on the API, and the page self-gates non-managers to an honest note rather than a guaranteed 403.
const stockRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stock',
  component: StockPage,
});
// WS-13 Feature 3a — prepaid packages / courses catalogue + liability. Manager-only in nav; every
// catalogue write and the liability report are requireManager on the API, and the page self-gates
// non-managers to an honest note rather than a guaranteed 403. Selling/redeeming happens at the till.
const packagesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/packages',
  component: PackagesPage,
});
// WS13 — reviews (LRU-05). NOT manager-only in nav: the list read is allowed for any staff role, so
// every authed user can see the reviews + the salon rating. Moderation/reply/settings/request are
// manager+ on the API, and the page self-gates non-managers to an honest note rather than a 403.
const reviewsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reviews',
  component: ReviewsPage,
});
// WS11 brief item 3 — the practitioner's identity-spanning "My week". No RBAC gate: every authed
// practitioner has a my-week keyed on their own session. The unified diary is a design-gated SCAFFOLD
// this slice; practitioner-level time off ("sick is sick everywhere") is a real surface beside it.
const myWeekRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/my-week',
  component: MyWeekPage,
});
// WS11 brief item — the practitioner's identity-spanning "My book": their OWN sealed clients unioned
// across renter residencies (the server read-side union, ADR-0012 §2), with a per-salon "Take your
// book" export as the clean exit. No RBAC gate — keyed on the caller's own session like My week.
const myBookRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/my-book',
  component: BookPage,
});
// WS6 brief item 7 — first-run, resumable onboarding wizard. The go-live checklist here is the
// landing target for WS3's signup funnel; the wizard self-gates to owner/manager.
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  component: OnboardingWizard,
});

// WS1 account-recovery — three PRE-session surfaces. They share rootRoute (so the basepath and the
// router shell apply) but render for ANONYMOUS visitors: AuthGate lets the Outlet through on these
// exact paths instead of falling back to Login (see ANON_PATHS in components/AuthGate.tsx). Each page
// is a self-contained full-page login-register panel, so no AppShell. The reset path MUST stay
// '/reset-password' to match the API's emailed link `${publicBaseUrl}/app/reset-password?token=`.
const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invite',
  component: AcceptInvitePage,
});
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
});

const routeTree = rootRoute.addChildren([
  diaryRoute,
  queueRoute,
  clientsRoute,
  takingsRoute,
  paymentsRoute,
  commsRoute,
  reportsRoute,
  settingsRoute,
  stockRoute,
  packagesRoute,
  reviewsRoute,
  myWeekRoute,
  myBookRoute,
  onboardingRoute,
  acceptInviteRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
]);

// Mirror Vite's base so client routing works whether served at the origin root or under /app/.
const basepath = import.meta.env.BASE_URL.replace(/\/$/, '');

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultErrorComponent: RouteErrorComponent,
  ...(basepath ? { basepath } : {}),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
